import React from 'react';
import { CalendarEvent, ViewType } from './types';
import EventCard from './EventCard';
import { formatDateWithOptions, parseDateFlexible } from '@/utils/Date';

const getEventKey = (event: CalendarEvent, suffix?: string | number) => {
  const keyParts = [
    event.assetType ?? 'none',
    event.id,
    event.status,
    event.berthNumber ?? '',
    event.addon?.id ?? '',
    event.service?.id ?? '',
  ];

  if (suffix !== undefined) keyParts.push(String(suffix));
  return keyParts.join('|');
};

interface CalendarGridProps {
  currentDate: Date;
  view: ViewType;
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent, date?: Date) => void;
  onMoreClick?: (date: Date, events: CalendarEvent[]) => void;
  showStatusLabel?: boolean;
  showAssetTypeLabel?: boolean;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  view,
  events,
  onEventClick,
  onMoreClick,
  showStatusLabel = true,
  showAssetTypeLabel = true,
}) => {
  // Normalize to local midnight (removes time component for day-level comparisons)
  const normalizeDate = React.useCallback(
    (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()),
    []
  );

  // Wrap parseDateFlexible to return normalized local-midnight timestamp for comparisons.
  const toNormalizedTs = React.useCallback(
    (value: string): number => {
      const dt = parseDateFlexible(value);
      if (!dt) return NaN;
      return normalizeDate(dt).getTime();
    },
    [normalizeDate]
  );

  // Precompute normalized timestamps for all events to avoid repeated parsing inside loops.
  const normalizedEvents = React.useMemo(
    () =>
      events.map((ev) => {
        const startTs = toNormalizedTs(ev.start);
        // If end missing, treat as open-ended into the future
        const endTs = ev.end
          ? toNormalizedTs(ev.end)
          : Number.POSITIVE_INFINITY;
        return { event: ev, startTs, endTs };
      }),
    [events, toNormalizedTs]
  );

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    const dayOfWeek = (firstDay.getDay() + 6) % 7;
    startDate.setDate(startDate.getDate() - dayOfWeek);

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getEventsForDate = (date: Date) => {
    const target = normalizeDate(date).getTime();
    return normalizedEvents
      .filter((ne) => target >= ne.startTs && target <= ne.endTs)
      .map((ne) => ne.event);
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return normalizeDate(date).getTime() === normalizeDate(today).getTime();
  };

  const isCurrentMonth = (date: Date) => {
    return (
      date.getMonth() === currentDate.getMonth() &&
      date.getFullYear() === currentDate.getFullYear()
    );
  };

  if (view === 'month') {
    const days = getDaysInMonth(currentDate);
    const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <div className="bg-gray-100 rounded-xl shadow-sm border border-gray-300 overflow-hidden">
        {/* Week headers */}
        <div className="grid grid-cols-7 border-b border-gray-300">
          {weekDays.map((day) => (
            <div
              key={day}
              className="p-4 text-center font-semibold text-gray-700 bg-gray-200"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = getEventsForDate(day);
            const isCurrentMonthDay = isCurrentMonth(day);
            const isTodayDay = isToday(day);

            return (
              <div
                key={index}
                className={`
                  min-h-[120px] p-2 border-r border-b border-gray-300 transition-colors
                  ${isCurrentMonthDay ? 'bg-gray-100' : 'bg-gray-200'}
                  ${isTodayDay ? 'bg-blue-50 dark:bg-gray-300' : ''}
                  hover:bg-gray-200
                `}
              >
                <div
                  className={`
                  text-sm font-medium mb-2
                  ${isCurrentMonthDay ? 'text-gray-900' : 'text-gray-600 dark:text-gray-700 contrast:text-gray-500'}
                  ${isTodayDay ? 'text-blue-600 dark:text-blue-200' : ''}
                `}
                >
                  {day.getDate()}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 2).map((event) => (
                    <EventCard
                      key={getEventKey(event)}
                      event={event}
                      onClick={() => onEventClick(event, day)}
                      showStatusLabel={showStatusLabel}
                      showAssetTypeLabel={showAssetTypeLabel}
                    />
                  ))}
                  {dayEvents.length > 2 && (
                    <button
                      type="button"
                      onClick={() => onMoreClick?.(day, dayEvents)}
                      className="text-xs font-medium text-blue-600 dark:text-blue-200 contrast:text-blue-200 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 rounded"
                      aria-label={`Show ${dayEvents.length - 2} more bookings`}
                    >
                      +{dayEvents.length - 2} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (view === 'week') {
    // Start of week (Monday)
    const start = new Date(currentDate);
    const dayOfWeek = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dayOfWeek);
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      days.push(d);
    }
    return (
      <div className="bg-gray-100 rounded-xl shadow-sm border border-gray-300 overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-200 border-b border-gray-300">
          {days.map((d, idx) => (
            <div
              key={idx}
              className="p-3 text-center text-xs font-semibold text-gray-600"
            >
              {formatDateWithOptions(d, { weekday: 'short' }, undefined)}
              <div className="text-sm font-medium mt-1 text-gray-900">
                {d.getDate()}
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 min-h-[320px]">
          {days.map((d, idx) => {
            const target = normalizeDate(d).getTime();
            const dayEvents = normalizedEvents
              .filter((ne) => target >= ne.startTs && target <= ne.endTs)
              .map((ne) => ne.event);
            const isToday = normalizeDate(new Date()).getTime() === target;
            return (
              <div
                key={idx}
                className={`border-r border-gray-300 p-2 ${isToday ? 'bg-blue-50 dark:bg-gray-300 contrast:bg-gray-300' : ''} last:border-r-0`}
              >
                <div className="space-y-1">
                  {dayEvents.length === 0 && (
                    <div
                      className={`text-[10px] text-gray-600 contrast:text-gray-800 italic ${isToday ? 'dark:text-blue-200 contrast:text-blue-200' : ''}`}
                    >
                      No bookings
                    </div>
                  )}
                  {dayEvents.map((ev) => (
                    <EventCard
                      key={getEventKey(ev, idx)}
                      event={ev}
                      onClick={() => onEventClick(ev, d)}
                      showStatusLabel={showStatusLabel}
                      showAssetTypeLabel={showAssetTypeLabel}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Day view
  if (view === 'day') {
    const day = currentDate;
    const target = normalizeDate(day).getTime();
    const dayEvents = normalizedEvents
      .filter((ne) => target >= ne.startTs && target <= ne.endTs)
      .map((ne) => ne.event);
    return (
      <div className="bg-gray-100 rounded-xl shadow-sm border border-gray-300 p-4 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            {formatDateWithOptions(day, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }, undefined)}
          </h2>
          <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
            {dayEvents.length} booking{dayEvents.length !== 1 && 's'}
          </span>
        </div>
        <div className="space-y-2">
          {dayEvents.length === 0 && (
            <div className="text-sm text-gray-500">
              No bookings for this day.
            </div>
          )}
          {dayEvents.map((ev) => (
            <EventCard
              key={getEventKey(ev)}
              event={ev}
              onClick={() => onEventClick(ev, day)}
              showStatusLabel={showStatusLabel}
              showAssetTypeLabel={showAssetTypeLabel}
            />
          ))}
        </div>
      </div>
    );
  }

  return null;
};

export default CalendarGrid;
